import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(undefined);

const STORAGE_KEY = 'palcare-theme';

/**
 * Determines the initial theme:
 * 1. Check localStorage for a persisted preference
 * 2. Fall back to the OS-level prefers-color-scheme media query
 * 3. Default to 'light'
 */
function getInitialTheme() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') return stored;
    } catch {
        // localStorage may be unavailable (private browsing, etc.)
    }
    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
}

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(getInitialTheme);

    // Apply the data-theme attribute on <html> whenever theme changes
    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute('data-theme', theme);

        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            // silently ignore storage errors
        }
    }, [theme]);

    // Listen for OS-level theme changes
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => {
            // Only auto-switch if user hasn't manually set a preference
            try {
                const stored = localStorage.getItem(STORAGE_KEY);
                if (!stored) {
                    setThemeState(e.matches ? 'dark' : 'light');
                }
            } catch {
                setThemeState(e.matches ? 'dark' : 'light');
            }
        };

        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, []);

    const toggleTheme = useCallback(() => {
        setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
    }, []);

    const setTheme = useCallback((newTheme) => {
        if (newTheme === 'light' || newTheme === 'dark') {
            setThemeState(newTheme);
        }
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}

export default ThemeContext;
